/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Component, EventEmitter, HostBinding, HostListener, Output, computed, inject, input } from '@angular/core';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { TooltipOptions } from 'primeng/api';

import { EnvironmentService, ENVIRONMENT, Environment } from '@environment';
import { attributeTranslations } from '@usheru-hff/sdk/attribute';

import { HourItem } from '../showtime-streaming-in-cinemas/performance/performance.component';

@Component({
  selector: 'ush-ui-hour',
  styleUrls: ['./ui-hour.component.scss'],
  standalone: false,
  template: `
    <a
      class="ush-hour-wrapper"
      [class.disabled]="isDisabled() || !link()"
      [attr.href]="link()"
      [attr.target]="link() ? '_blank' : null"
      [attr.data-date]="hourISODate()"
    >
      <div
        class="attributes-wrapper"
        [pTooltip]="attributesTooltip()"
        [appendTo]="hourTooltipContainer"
        [tooltipOptions]="hintOptions"
        [tooltipDisabled]="!hasHiddenAttributes()"
      >
        @if (attributesEnabled()) {
          @for (attr of visibleAttributes(); track attr) {
            @if (attr) {
              <ush-ui-attribute [attribute]="attr"></ush-ui-attribute>
            }
          }
        }
        <fa-icon [icon]="arrowRightIcon"></fa-icon>
      </div>
      <span #hourTooltipContainer class="hour-tooltip-container"></span>

      <span>{{ hour()?.hour }}</span>
      @if (hour()?.showtimeStatusMessage) {
        <span class="hour-description" [attr.data-translate]="hour()?.showtimeStatusMessage?.key">{{
          hour()?.showtimeStatusMessage?.label
        }}</span>
      }
      @if (hour()?.showtimeDescription) {
        <div class="showtime-description" [innerHTML]="hour()?.showtimeDescription | safe: 'html'">
          {{ hour()?.showtimeDescription }}
        </div>
      }
    </a>
  `
})
export class UiHourComponent {
  private static readonly DEFAULT_MAX_VISIBLE_ATTRIBUTES = 1;

  @HostBinding('id') id!: string;
  @HostBinding('type') type = 'button';
  @HostBinding('role') role = 'button';
  @HostBinding('tabindex') tabindex = '0';
  @HostBinding('class.disabled') get disabledClass() {
    return this.isDisabled();
  }

  protected readonly hintOptions: TooltipOptions = {
    tooltipPosition: 'right',
    tooltipStyleClass: 'ush-hour-attributes-tooltip',
    showDelay: 250
  };

  @HostListener('click') onClick() {
    if (this.isDisabled()) return;
    this.hourSelected.emit(this.hour());
  }

  @HostListener('keydown.enter') onEnter() {
    if (this.isDisabled()) return;
    this.hourSelected.emit(this.hour());
  }

  @HostListener('keyup', ['$event']) onKeyup(event: KeyboardEvent) {
    // on arrows right or left move focus to the next or previous hour
    const target = event.target as HTMLElement;
    if (event.key === 'ArrowRight') {
      const nextSibling = target?.nextElementSibling as HTMLElement;
      if (nextSibling) {
        nextSibling?.focus();
      } else {
        const parent = target?.parentElement as HTMLElement;
        const firstChild = parent?.firstElementChild as HTMLElement;
        firstChild?.focus();
      }
    }
    if (event.key === 'ArrowLeft') {
      const previousSibling = target?.previousElementSibling as HTMLElement;
      if (previousSibling) {
        previousSibling?.focus();
      } else {
        const parent = target?.parentElement as HTMLElement;
        const lastChild = parent?.lastElementChild as HTMLElement;
        lastChild?.focus();
      }
    }
  }

  hour = input.required<HourItem>();

  hourISODate = computed(() => this.hour().hourDate.toISOString());

  readonly isDisabled = computed(() => this.hour().disabled);
  readonly link = computed(() => (!this.isDisabled() ? this.createLink(this.hour()) : null));

  @Output() hourSelected = new EventEmitter<HourItem>();

  arrowRightIcon = faArrowRight;
  private env: Environment = inject(ENVIRONMENT);
  private readonly environmentService = inject(EnvironmentService);
  readonly maxVisibleAttributes =
    this.environmentService.showtimeStreamingInCinemasComponent('maxVisiblePerformanceHourAttributes') ??
    UiHourComponent.DEFAULT_MAX_VISIBLE_ATTRIBUTES;
  readonly hourAttributes = computed(() => this.hour().attributes?.filter((attr): attr is string => !!attr) ?? []);
  readonly visibleAttributes = computed(() => this.hourAttributes().slice(0, this.maxVisibleAttributes));
  readonly hasHiddenAttributes = computed(() => this.hourAttributes().length > this.maxVisibleAttributes);
  readonly attributesTooltip = computed(() =>
    this.hasHiddenAttributes()
      ? this.hourAttributes()
          .map(attr => attributeTranslations[attr] || attr)
          .join(', ')
      : ''
  );
  readonly attributesEnabled = computed(
    () => this.env.showtimeStreamingConfig?.performanceHourAttributes != false && this.hourAttributes().length > 0
  );

  noLinkWarningShown = false;

  createLink(hour: HourItem) {
    const showtimeLink = hour?.redirectionLink || '';
    if (showtimeLink) {
      if (showtimeLink.indexOf('http://') == 0 || showtimeLink.indexOf('https://') == 0) {
        return showtimeLink;
      } else {
        return 'http://' + showtimeLink;
      }
    }

    if (!this.noLinkWarningShown) {
      console.warn('No showtime link available for this hour', {
        performance: hour.performance,
        statusMessage: hour.showtimeStatusMessage,
        hour: hour.hour,
        date: hour.hourDate.toISOString()
      });
      this.noLinkWarningShown = true;
    }
  }
}
